import type { ManualCardPropsInterface } from '~/types/AppData/PageBuilder/CardBlockInterface';
import type { ArticleCoverType } from '~/types/AppData/PageContent/ArticleCoverInterface';
import type { ArticleInterface } from '~/types/AppData/PageContent/ArticleInterface';
import { getImagePathAndExtension } from '~/utilities/getImagePathAndExtension';
import { formatDateForDisplay } from './formatDateForDisplay';
import { getArticleAuthors } from './getArticleAuthors';
import type { CardContentInterface } from '~/types/CardContentInterface';
import type { AuthorInterface } from '~/types/AppData/PageContent/AuthorInterface';
import { getImageCroppingObject } from '~/utilities/getImageCroppingObject';

const getManualCardContent = (props: ManualCardPropsInterface) => {
  const { title, date, link, imageUrl, alt, author } = props;
  const { path, extension } = getImagePathAndExtension(imageUrl);

  const cropping = getImageCroppingObject(imageUrl);

  return {
    title,
    authors: [{ name: author }] as AuthorInterface[],
    formattedDate: date,
    cardLink: link,
    coverType: 'image' as ArticleCoverType,
    cover: {
      image: {
        value: {
          caption: '',
          credit: '',
          cropping,
          path,
          fileExtension: path ? extension : '',
          description: path ? title : '',
          alt,
        },
      },
    },
  };
};

const getArticleCardContent = (
  content: ArticleInterface,
  props?: ManualCardPropsInterface
) => {
  const {
    title,
    seoDescription,
    cover: originalCover,
    coverType,
    articleUrl,
    updatedAt,
    createdAt,
    mainCategory,
  } = content;
  const authors = getArticleAuthors(
    content.authors,
    content.metadata?.hideOwner
  );
  const {
    image: { imageURL, altText } = { imageURL: '', altText: '' },
    title: propsTitle,
  } = props || {};
  let cover = originalCover;

  if (imageURL) {
    const { path, extension } = imageURL
      ? getImagePathAndExtension(imageURL)
      : { path: '', extension: '' };

    const cropping = imageURL
      ? getImageCroppingObject(imageURL)
      : originalCover.image.value.cropping;

    cover = {
      image: {
        value: {
          ...originalCover.image.value,
          alt: altText,
          cropping,
          path,
          fileExtension: extension,
          description: title,
        },
      },
    };
  }

  return {
    title: propsTitle || title,
    seoDescription,
    cover,
    coverType: imageURL ? 'image' : coverType,
    cardLink: articleUrl,
    formattedDate: formatDateForDisplay(updatedAt),
    unformattedDate: updatedAt,
    createdAtFormattedDate: formatDateForDisplay(createdAt),
    createdAtUnformattedDate: createdAt,
    mainCategory,
    authors,
  };
};

export const getCardContent = (
  content?: ArticleInterface[],
  props?: ManualCardPropsInterface
): CardContentInterface | null => {
  if (props && props.imageUrl) {
    return getManualCardContent(props);
  }

  if (content?.length) {
    return getArticleCardContent(content[0], props);
  }

  return null;
};
